import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box, Typography, Button, Paper, Table, TableHead, TableBody, TableRow, TableCell, TableContainer, Chip, IconButton, Tooltip,
  Alert, CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions, TextField, FormControl, InputLabel, Select,
  MenuItem, Tabs, Tab, Autocomplete, Avatar, Divider, Stack, Checkbox, FormControlLabel, Menu, ListItemIcon, ListItemText,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import LinkIcon from '@mui/icons-material/Link';
import CheckroomIcon from '@mui/icons-material/Checkroom';
import AssessmentIcon from '@mui/icons-material/Assessment';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import TuneIcon from '@mui/icons-material/Tune';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import LeaderEmailDialog from './LeaderEmailDialog';
import ContractTemplateDialog from './ContractTemplateDialog';
import DescriptionIcon from '@mui/icons-material/Description';
import { adminClient } from '../data/adminClient';
import WorkshopFieldsEditor from '../../shared/WorkshopFieldsEditor';
import SizeDietaryFields from '../../shared/SizeDietaryFields';
import { toPrintableImage } from '../../shared/imageUtils';
import { TSHIRT_SIZES } from '../../shared/participantOptions';
import {
  LEADER_STATUSES, ALLOWED_SESSIONS, ALLOWED_SESSIONS_LABELS, CONTRACT_STATUSES,
  PHOTO_ACCEPT, MAX_PHOTO_BYTES, emptyWorkshop, leaderLinkUrl, musicLabel, isConfirmedStatus,
} from '../../shared/workshopOptions';
import { useEventReadOnly } from '../adminAccess';

const STATUS_COLOR = { Planned: 'default', Invited: 'warning', Confirmed: 'info', 'Info Complete': 'success', Declined: 'error' };
const CONTRACT_COLOR = { 'Not started': 'default', Draft: 'warning', Sent: 'info', Signed: 'success' };
const shortDate = (iso) => (iso ? new Date(iso).toLocaleDateString('en-CA', { month: 'short', day: 'numeric' }) : '');

// Admin › Workshops › Workshop Leaders. This event's leaders (profile +
// this event's engagement + workshops). Profiles are kept across events, so
// returning leaders can be re-invited with their bio/photo intact.
export default function LeadersSection() {
  // View-only admin: no adding, emailing, links, contracts, equipment or pay —
  // rows open a read-only summary (the server also leaves pay and the
  // private link token out of what it sends).
  const readOnly = useEventReadOnly();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState('');
  const [addOpen, setAddOpen] = useState(false);
  const [sizesOpen, setSizesOpen] = useState(false);
  const [reportsAnchor, setReportsAnchor] = useState(null);
  const [equipmentOpen, setEquipmentOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [selected, setSelected] = useState(() => new Set());
  const [emailRecipients, setEmailRecipients] = useState(null); // leaders to email, or null
  const [emailTemplateId, setEmailTemplateId] = useState(null);
  const [contractOpen, setContractOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await adminClient.getWorkshopLeaders());
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const leaders = data?.leaders || [];
  const editing = leaders.find(l => l.engagementId === editingId) || null;
  const confirmedCount = leaders.filter(l => isConfirmedStatus(l.status)).length;
  const selectedLeaders = leaders.filter(l => selected.has(l.engagementId));
  const allSelected = leaders.length > 0 && selectedLeaders.length === leaders.length;
  const toggleSelected = (id) => setSelected(prev => { const next = new Set(prev); next.has(id) ? next.delete(id) : next.add(id); return next; });
  const openEmail = (recipients, templateId = null) => { setEmailTemplateId(templateId); setEmailRecipients(recipients); };

  const copyLink = async (leader) => {
    try {
      await navigator.clipboard.writeText(leaderLinkUrl(leader.linkToken));
      setNotice(`Copied ${leader.profile.name}'s private link — send it to them so they can add their bio, photo and workshop details.`);
    } catch {
      setError('Could not copy to the clipboard');
    }
  };

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
        <Typography variant="body2" color="text.secondary" sx={{ flex: 1, minWidth: 200 }}>
          {data ? `${leaders.length} leader${leaders.length === 1 ? '' : 's'} for ${data.eventName || 'this event'} · ${confirmedCount} confirmed` : ''}
        </Typography>
        <Tooltip title="Refresh"><span><IconButton size="small" onClick={load} disabled={loading}><RefreshIcon fontSize="small" /></IconButton></span></Tooltip>
        {!readOnly && <Button size="small" variant="outlined" startIcon={<DescriptionIcon />} onClick={() => setContractOpen(true)} disabled={!data}>Contract template</Button>}
        {!readOnly && <Button size="small" variant="outlined" startIcon={<TuneIcon />} onClick={() => setEquipmentOpen(true)} disabled={!data}>Equipment list</Button>}
        <Button size="small" variant="outlined" startIcon={<AssessmentIcon />} endIcon={<ArrowDropDownIcon />}
          onClick={e => setReportsAnchor(e.currentTarget)} disabled={!leaders.length}>Reports</Button>
        <Menu anchorEl={reportsAnchor} open={!!reportsAnchor} onClose={() => setReportsAnchor(null)}>
          <MenuItem onClick={() => { setReportsAnchor(null); setSizesOpen(true); }}>
            <ListItemIcon><CheckroomIcon fontSize="small" /></ListItemIcon>
            <ListItemText primary="T-shirts & dietary" secondary="View on screen, copy to a spreadsheet" />
          </MenuItem>
          <Divider />
          {[
            { type: 'bios', label: 'Bios & headshots', secondary: 'PDF — photo, workshops and bio for each leader' },
            { type: 'tshirts', label: 'T-shirt sizes', secondary: 'PDF — totals by size, then each leader' },
            { type: 'dietary', label: 'Dietary needs', secondary: 'PDF — leaders with dietary needs or allergies' },
            !readOnly && { type: 'payments', label: 'Payments', secondary: 'PDF — e-transfer or cheque, pay and e-transfer emails' },
          ].filter(Boolean).map(r => (
            <MenuItem key={r.type} onClick={() => { setReportsAnchor(null); adminClient.openLeaderReport(r.type); }}>
              <ListItemIcon><PictureAsPdfIcon fontSize="small" /></ListItemIcon>
              <ListItemText primary={r.label} secondary={r.secondary} />
            </MenuItem>
          ))}
        </Menu>
        {!readOnly && <Button size="small" variant="contained" startIcon={<AddIcon />} onClick={() => setAddOpen(true)}>Add leader</Button>}
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {notice && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setNotice('')}>{notice}</Alert>}
      {selectedLeaders.length > 0 && (
        <Paper variant="outlined" sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', p: 1, mb: 1.5, bgcolor: 'action.hover' }}>
          <Typography variant="body2" sx={{ flex: 1, minWidth: 140, pl: 1 }}>{selectedLeaders.length} selected</Typography>
          <Button size="small" variant="contained" startIcon={<MailOutlineIcon />} onClick={() => openEmail(selectedLeaders)}>Email</Button>
          <Button size="small" onClick={() => setSelected(new Set())}>Clear</Button>
        </Paper>
      )}

      {loading && !data ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
      ) : leaders.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary" sx={{ mb: 2 }}>No workshop leaders for this event yet.</Typography>
          {!readOnly && <Button variant="contained" startIcon={<AddIcon />} onClick={() => setAddOpen(true)}>Add leader</Button>}
        </Paper>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                {!readOnly && <TableCell padding="checkbox">
                  <Checkbox size="small" checked={allSelected} indeterminate={selectedLeaders.length > 0 && !allSelected}
                    onChange={() => setSelected(allSelected ? new Set() : new Set(leaders.map(l => l.engagementId)))} />
                </TableCell>}
                <TableCell>Leader</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Sessions</TableCell>
                <TableCell>Workshops</TableCell>
                <TableCell>Contract</TableCell>
                <TableCell>Emailed</TableCell>
                {!readOnly && <TableCell align="right">Link</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {leaders.map(l => (
                <TableRow key={l.engagementId} hover selected={selected.has(l.engagementId)} sx={{ cursor: 'pointer' }} onClick={() => setEditingId(l.engagementId)}>
                  {!readOnly && <TableCell padding="checkbox" onClick={e => e.stopPropagation()}>
                    <Checkbox size="small" checked={selected.has(l.engagementId)} onChange={() => toggleSelected(l.engagementId)} />
                  </TableCell>}
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Avatar src={l.profile.photoUrl || undefined} sx={{ width: 32, height: 32 }}>{l.profile.name?.[0]}</Avatar>
                      <Box>
                        <Typography variant="body2" fontWeight={500}>{l.profile.name}</Typography>
                        {l.workshops.some(w => w.style) && (
                          <Typography variant="caption" color="text.secondary">{[...new Set(l.workshops.map(w => w.style).filter(Boolean))].join(', ')}</Typography>
                        )}
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip size="small" label={l.status} color={STATUS_COLOR[l.status]} variant="outlined" />
                    {l.status === 'Confirmed' && l.missingInfo.length > 0 && (
                      <Tooltip title={`Still needed: ${l.missingInfo.join(', ')}`}>
                        <Typography variant="caption" color="text.secondary" display="block">{l.missingInfo.length} item{l.missingInfo.length === 1 ? '' : 's'} to come</Typography>
                      </Tooltip>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{ALLOWED_SESSIONS_LABELS[l.allowedSessions]}</Typography>
                    {l.leaderChoosesSessions && <Typography variant="caption" color="text.secondary">Leader chooses</Typography>}
                  </TableCell>
                  <TableCell>
                    {l.workshops.length === 0
                      ? <Typography variant="caption" color="text.disabled">None yet</Typography>
                      : l.workshops.map(w => <Typography key={w.workshopId} variant="body2">{w.title}</Typography>)}
                  </TableCell>
                  <TableCell><Chip size="small" label={l.contractStatus} color={CONTRACT_COLOR[l.contractStatus]} variant="outlined" /></TableCell>
                  <TableCell>
                    {!l.lastEmailedAt && !l.inviteEmailedAt && !l.linkEmailedAt && <Typography variant="caption" color="text.disabled">—</Typography>}
                    {l.lastEmailedAt && (
                      <Tooltip title={[l.inviteEmailedAt && `Asked if interested ${shortDate(l.inviteEmailedAt)}`, l.linkEmailedAt && `Info link sent ${shortDate(l.linkEmailedAt)}`].filter(Boolean).join(' · ') || ''}>
                        <Typography variant="caption" display="block">{l.lastEmailName} · {shortDate(l.lastEmailedAt)}</Typography>
                      </Tooltip>
                    )}
                    {!l.lastEmailedAt && l.inviteEmailedAt && <Typography variant="caption" display="block">Asked {shortDate(l.inviteEmailedAt)}</Typography>}
                    {!l.lastEmailedAt && l.linkEmailedAt && <Typography variant="caption" display="block">Link sent {shortDate(l.linkEmailedAt)}</Typography>}
                  </TableCell>
                  {!readOnly && <TableCell align="right" onClick={e => e.stopPropagation()}>
                    <Tooltip title="Copy this leader's private info link">
                      <IconButton size="small" onClick={() => copyLink(l)}><LinkIcon fontSize="small" /></IconButton>
                    </Tooltip>
                  </TableCell>}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <AddLeaderDialog open={addOpen} onClose={() => setAddOpen(false)}
        onAdded={async (engagementId) => { setAddOpen(false); await load(); setEditingId(engagementId); }} />
      <SizesDietaryDialog open={sizesOpen} onClose={() => setSizesOpen(false)} leaders={leaders} />
      <ContractTemplateDialog open={contractOpen} onClose={() => setContractOpen(false)} leaders={leaders} />
      {data && (
        <EquipmentDialog open={equipmentOpen} onClose={() => setEquipmentOpen(false)} options={data.equipmentOptions}
          onSaved={(equipmentOptions) => { setData(d => ({ ...d, equipmentOptions })); setEquipmentOpen(false); }} />
      )}
      {editing && readOnly && <LeaderViewDialog leader={editing} onClose={() => setEditingId(null)} />}
      {editing && !readOnly && (
        <LeaderDialog key={editing.engagementId} leader={editing} equipmentOptions={data.equipmentOptions}
          onClose={() => setEditingId(null)} onChanged={load} onCopyLink={() => copyLink(editing)}
          onEmail={(templateId) => openEmail([editing], templateId)}
          onRemoved={async () => { setEditingId(null); await load(); }} />
      )}
      {/* After LeaderDialog so it opens on top when started from a leader. */}
      {emailRecipients && (
        <LeaderEmailDialog open recipients={emailRecipients} initialTemplateId={emailTemplateId} onClose={() => setEmailRecipients(null)} onSent={load} />
      )}
    </>
  );
}

// Read-only summary of a leader for view-only admins — profile and
// workshops only (no pay, contract or private link).
function LeaderViewDialog({ leader, onClose }) {
  const p = leader.profile;
  const fields = [
    { label: 'Status', value: leader.status },
    { label: 'Sessions', value: ALLOWED_SESSIONS_LABELS[leader.allowedSessions] },
    { label: 'Email', value: p.email },
    { label: 'Phone', value: p.phone },
    { label: 'T-shirt', value: p.tshirtSize },
    { label: 'Dietary', value: p.dietary },
  ].filter(f => f.value);
  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Avatar src={p.photoUrl || undefined} sx={{ width: 44, height: 44 }}>{p.name?.[0]}</Avatar>
        {p.name}
      </DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5, mb: 2 }}>
          {fields.map(({ label, value }) => (
            <Box key={label}>
              <Typography variant="caption" color="text.secondary" display="block">{label}</Typography>
              <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>{value}</Typography>
            </Box>
          ))}
        </Box>
        {p.bio && (
          <>
            <Typography variant="caption" color="text.secondary" display="block">Bio</Typography>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mb: 2 }}>{p.bio}</Typography>
          </>
        )}
        <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>Workshops</Typography>
        {leader.workshops.length === 0 && <Typography variant="body2" color="text.disabled">None yet</Typography>}
        {leader.workshops.map(w => (
          <Paper key={w.workshopId} variant="outlined" sx={{ p: 1.5, mb: 1 }}>
            <Typography variant="body2" fontWeight={600}>{w.title}</Typography>
            <Typography variant="caption" color="text.secondary" display="block">{[w.style, musicLabel(w.musicSetup)].filter(Boolean).join(' · ')}</Typography>
            {w.description && <Typography variant="body2" sx={{ mt: 0.5, whiteSpace: 'pre-wrap' }}>{w.description}</Typography>}
            {w.equipmentNeeded?.length > 0 && <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>Needs: {w.equipmentNeeded.join(', ')}</Typography>}
          </Paper>
        ))}
      </DialogContent>
      <DialogActions><Button variant="contained" onClick={onClose}>Close</Button></DialogActions>
    </Dialog>
  );
}

function AddLeaderDialog({ open, onClose, onAdded }) {
  const [tab, setTab] = useState(0);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [profiles, setProfiles] = useState([]);
  const [returning, setReturning] = useState(null);
  const [sessions, setSessions] = useState('Either');
  const [workshopTitle, setWorkshopTitle] = useState('');
  const [workshopStyle, setWorkshopStyle] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) return;
    setTab(0); setName(''); setEmail(''); setReturning(null); setError(null);
    setSessions('Either'); setWorkshopTitle(''); setWorkshopStyle('');
    adminClient.getWorkshopProfiles().then(setProfiles).catch(err => setError(err.message));
  }, [open]);

  const available = profiles.filter(p => !p.inCurrentEvent);
  const canAdd = tab === 0 ? name.trim() : !!returning;

  const handleAdd = async () => {
    setSaving(true);
    setError(null);
    try {
      const who = tab === 0 ? { name: name.trim(), email: email.trim() } : { leaderId: returning.leaderId };
      const workshop = workshopTitle.trim() ? { title: workshopTitle.trim(), style: workshopStyle.trim() } : undefined;
      const { engagementId } = await adminClient.addWorkshopLeader({ ...who, allowedSessions: sessions, workshop });
      onAdded(engagementId);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Add workshop leader</DialogTitle>
      <DialogContent>
        <Tabs value={tab} onChange={(e, v) => setTab(v)} sx={{ mb: 2 }}>
          <Tab label="New leader" />
          <Tab label={`Returning leader${available.length ? ` (${available.length})` : ''}`} />
        </Tabs>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {tab === 0 ? (
          <Stack spacing={2}>
            <TextField label="Name" size="small" required autoFocus value={name} onChange={e => setName(e.target.value)} />
            <TextField label="Email" size="small" type="email" value={email} onChange={e => setEmail(e.target.value)}
              helperText="They can fill in everything else themselves through their private link." />
            <Typography variant="caption" color="text.secondary">
              Adding a leader doesn’t contact them. They’re marked <strong>Planned</strong>, so you can add the workshops you’d like
              them to teach and put them on the schedule first. Emailing them later marks them Invited.
            </Typography>
          </Stack>
        ) : (
          <Autocomplete
            options={available}
            value={returning}
            onChange={(e, v) => setReturning(v)}
            getOptionLabel={(p) => p.name}
            isOptionEqualToValue={(a, b) => a.leaderId === b.leaderId}
            renderOption={(props, p) => (
              <li {...props} key={p.leaderId}>
                <Box>
                  <Typography variant="body2">{p.name}</Typography>
                  <Typography variant="caption" color="text.secondary">{[p.style, `${p.eventCount} past event${p.eventCount === 1 ? '' : 's'}`].filter(Boolean).join(' · ')}</Typography>
                </Box>
              </li>
            )}
            noOptionsText="No saved leaders who aren't already in this event"
            renderInput={(params) => <TextField {...params} size="small" label="Search saved leaders" helperText="Their bio and photo carry over; they get a new link for this event." />}
          />
        )}
        <Divider sx={{ my: 2 }} />
        <Stack spacing={2}>
          <FormControl size="small" fullWidth>
            <InputLabel>Sessions</InputLabel>
            <Select label="Sessions" value={sessions} onChange={e => setSessions(e.target.value)}>
              {ALLOWED_SESSIONS.map(s => <MenuItem key={s} value={s}>{ALLOWED_SESSIONS_LABELS[s]}</MenuItem>)}
            </Select>
          </FormControl>
          <Box sx={{ display: 'grid', gridTemplateColumns: '3fr 2fr', gap: 1.5 }}>
            <TextField label="Workshop title (optional)" size="small" value={workshopTitle} onChange={e => setWorkshopTitle(e.target.value)} inputProps={{ maxLength: 120 }} />
            <TextField label="Dance style" size="small" value={workshopStyle} onChange={e => setWorkshopStyle(e.target.value)} inputProps={{ maxLength: 80 }} />
          </Box>
          <Typography variant="caption" color="text.secondary">
            Enough to put them on the schedule. Add more workshops or details afterwards.
          </Typography>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleAdd} disabled={!canAdd || saving}>{saving ? <CircularProgress size={18} /> : 'Add'}</Button>
      </DialogActions>
    </Dialog>
  );
}

function LeaderDialog({ leader, equipmentOptions, onClose, onChanged, onRemoved, onCopyLink, onEmail }) {
  const [profile, setProfile] = useState(leader.profile);
  const [engagement, setEngagement] = useState({
    status: leader.status, allowedSessions: leader.allowedSessions, leaderChoosesSessions: leader.leaderChoosesSessions,
    leaderCanAddWorkshops: leader.leaderCanAddWorkshops !== false,
    paymentMethod: leader.paymentMethod || '', etransferEmail: leader.etransferEmail || '',
    compensation: leader.compensation,
    compensationNotes: leader.compensationNotes, contractStatus: leader.contractStatus,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [savedMsg, setSavedMsg] = useState('');
  const [photoBusy, setPhotoBusy] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [editingWorkshop, setEditingWorkshop] = useState(null); // workshop object being edited/added
  const [tab, setTab] = useState('teaching');
  const [emptied, setEmptied] = useState(null); // schedule slots freed by declining
  const fileRef = useRef(null);

  const setP = (key) => (e) => setProfile(p => ({ ...p, [key]: e.target.value }));
  const setE = (key) => (e) => setEngagement(x => ({ ...x, [key]: e.target.value }));

  const handleSave = async () => {
    setSaving(true); setError(null); setSavedMsg('');
    try {
      const { photoUrl: _photo, ...profileFields } = profile;
      // Payment is usually set by the leader on their link — only send it if
      // it was changed here, so an open dialog can't wipe their choice.
      const { paymentMethod, etransferEmail, ...rest } = engagement;
      const paymentChanged = paymentMethod !== (leader.paymentMethod || '') || etransferEmail !== (leader.etransferEmail || '');
      const { emptied: freed } = await adminClient.saveWorkshopLeader(leader.engagementId, {
        profile: profileFields,
        engagement: paymentChanged ? engagement : rest,
      });
      // Declined: their workshops came off the schedule — show what's now empty.
      if (freed?.length) setEmptied(freed);
      setSavedMsg('Saved');
      onChanged();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handlePhoto = async (picked) => {
    // WebP / very large photos become a JPEG so they can print in the bios PDF.
    const file = picked && await toPrintableImage(picked);
    if (file && (!PHOTO_ACCEPT.split(',').includes(file.type) || file.size > MAX_PHOTO_BYTES)) {
      setError('Photos must be JPG, PNG or WebP, 5MB or smaller.');
      return;
    }
    setPhotoBusy(true); setError(null);
    try {
      const { photoUrl } = await adminClient.setWorkshopLeaderPhoto(leader.engagementId, file);
      setProfile(p => ({ ...p, photoUrl }));
      onChanged();
    } catch (err) {
      setError(err.message);
    } finally {
      setPhotoBusy(false);
    }
  };

  const handleRemove = async () => {
    setSaving(true); setError(null);
    try {
      await adminClient.removeWorkshopLeader(leader.engagementId);
      onRemoved();
    } catch (err) {
      setError(err.message);
      setSaving(false);
    }
  };

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
        {leader.profile.name}
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          <Button size="small" startIcon={<MailOutlineIcon />} onClick={() => onEmail()}>Email</Button>
          <Button size="small" startIcon={<LinkIcon />} onClick={onCopyLink}>Copy link</Button>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

        <Tabs value={tab} onChange={(e, v) => setTab(v)} sx={{ mb: 2, mt: -1, borderBottom: 1, borderColor: 'divider' }}>
          <Tab value="teaching" label={`Teaching (${leader.workshops.length} workshop${leader.workshops.length === 1 ? '' : 's'})`} />
          <Tab value="profile" label="Profile" />
          <Tab value="contract" label="Pay & contract" />
        </Tabs>

        {tab === 'teaching' && (
          <>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mb: 2.5 }}>
              <Box>
                <FormControl size="small" fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select label="Status" value={engagement.status} onChange={setE('status')}>
                    {/* Planned │ Invited · Confirmed · Info Complete │ Declined */}
                    {LEADER_STATUSES.flatMap(s => [
                      ...(s === 'Invited' || s === 'Declined' ? [<Divider key={`divider-${s}`} />] : []),
                      <MenuItem key={s} value={s} disabled={s === 'Info Complete' && leader.missingInfo.length > 0}>
                        {s}
                      </MenuItem>,
                    ])}
                  </Select>
                </FormControl>
                {isConfirmedStatus(engagement.status) && (
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                    {leader.missingInfo.length
                      ? `Still needed for Info Complete: ${leader.missingInfo.join(', ')}.`
                      : 'All their info is in.'}
                  </Typography>
                )}
              </Box>
              <Box>
                <FormControl size="small" fullWidth>
                  <InputLabel>Sessions</InputLabel>
                  <Select label="Sessions" value={engagement.allowedSessions} onChange={setE('allowedSessions')}>
                    {ALLOWED_SESSIONS.map(s => <MenuItem key={s} value={s}>{ALLOWED_SESSIONS_LABELS[s]}</MenuItem>)}
                  </Select>
                </FormControl>
                <FormControlLabel sx={{ mt: 0.5 }}
                  control={<Checkbox size="small" checked={!!engagement.leaderChoosesSessions}
                    onChange={e => setEngagement(x => ({ ...x, leaderChoosesSessions: e.target.checked }))} />}
                  label={<Typography variant="body2">Let the leader choose on their link</Typography>} />
                <Typography variant="caption" color="text.secondary" display="block">
                  {engagement.leaderChoosesSessions
                    ? 'Their link asks when they’re available; their answer updates Sessions.'
                    : 'Their link shows the session(s) set here.'}
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Typography fontWeight={600}>Workshops</Typography>
              <Button size="small" startIcon={<AddIcon />} onClick={() => setEditingWorkshop(emptyWorkshop())}>Add workshop</Button>
            </Box>
            <FormControlLabel sx={{ mb: 0.5 }}
              control={<Checkbox size="small" checked={!!engagement.leaderCanAddWorkshops}
                onChange={e => setEngagement(x => ({ ...x, leaderCanAddWorkshops: e.target.checked }))} />}
              label={<Typography variant="body2">Leader can add or remove workshops on their link</Typography>} />
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
              {engagement.leaderCanAddWorkshops
                ? 'They can add their own workshops and remove ones listed here.'
                : 'They can only fill in details (description, music, equipment) for the workshops listed here.'}
            </Typography>
            {leader.workshops.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                {engagement.leaderCanAddWorkshops
                  ? 'No workshops yet. Add one here, or the leader can add their own through their link.'
                  : 'No workshops yet. Add the workshop(s) you’d like them to teach — they’ll fill in the details.'}
              </Typography>
            ) : (
              <Stack spacing={1}>
                {leader.workshops.map(w => (
                  <Paper key={w.workshopId} variant="outlined" sx={{ p: 1.5, cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
                    onClick={() => setEditingWorkshop({ ...w, maxDancers: w.maxDancers ?? '' })}>
                    <Typography variant="body2" fontWeight={600}>{w.title}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {[w.style, w.maxDancers ? `max ${w.maxDancers}` : null, musicLabel(w.musicSetup),
                        w.equipmentNeeded.length ? `equipment: ${w.equipmentNeeded.join(', ')}` : null].filter(Boolean).join(' · ') || 'Click to add details'}
                    </Typography>
                  </Paper>
                ))}
              </Stack>
            )}
            {leader.workshops.length > 1 && (
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                {engagement.allowedSessions === 'Both Sessions'
                  ? 'Teaching both sessions — the schedule can use a different workshop in each.'
                  : 'Teaching one session — these are options; the schedule uses one of them.'}
              </Typography>
            )}
          </>
        )}

        {tab === 'profile' && (
        <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
            <Avatar src={profile.photoUrl || undefined} variant="rounded" sx={{ width: 120, height: 120, fontSize: 40 }}>{profile.name?.[0]}</Avatar>
            <input ref={fileRef} type="file" accept={PHOTO_ACCEPT} hidden onChange={e => { const f = e.target.files?.[0]; e.target.value = ''; if (f) handlePhoto(f); }} />
            <Button size="small" onClick={() => fileRef.current?.click()} disabled={photoBusy}>{photoBusy ? 'Uploading…' : profile.photoUrl ? 'Replace photo' : 'Upload photo'}</Button>
            {profile.photoUrl && <Button size="small" color="error" onClick={() => handlePhoto(null)} disabled={photoBusy}>Remove</Button>}
          </Box>
          <Box sx={{ flex: 1, minWidth: 260, display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
            <TextField label="Name" size="small" required value={profile.name} onChange={setP('name')} sx={{ gridColumn: '1 / -1' }} />
            <TextField label="Email" size="small" value={profile.email} onChange={setP('email')} />
            <TextField label="Phone" size="small" value={profile.phone} onChange={setP('phone')} />
            <TextField label="Bio" size="small" multiline minRows={4} value={profile.bio} onChange={setP('bio')} sx={{ gridColumn: '1 / -1' }}
              helperText="Posted publicly with their photo." />
            <Box sx={{ gridColumn: '1 / -1' }}>
              <SizeDietaryFields value={profile} onChange={(changes) => setProfile(p => ({ ...p, ...changes }))} />
            </Box>
            <TextField label="Admin notes (never shown to the leader)" size="small" multiline minRows={2} value={profile.adminNotes}
              onChange={setP('adminNotes')} sx={{ gridColumn: '1 / -1' }} />
          </Box>
        </Box>
        )}

        {tab === 'contract' && (
          <>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 2, mb: 3 }}>
              <TextField label="Compensation ($)" size="small" value={engagement.compensation} onChange={setE('compensation')} />
              <TextField label="Compensation notes" size="small" value={engagement.compensationNotes} onChange={setE('compensationNotes')} sx={{ gridColumn: { sm: 'span 2' } }} />
              <FormControl size="small">
                <InputLabel>Contract</InputLabel>
                <Select label="Contract" value={engagement.contractStatus} onChange={setE('contractStatus')}>
                  {CONTRACT_STATUSES.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                </Select>
              </FormControl>
              {/* Usually chosen by the leader on their link (contract section). */}
              <FormControl size="small">
                <InputLabel>Paid by</InputLabel>
                <Select label="Paid by" value={engagement.paymentMethod}
                  onChange={e => setEngagement(x => ({
                    ...x, paymentMethod: e.target.value,
                    etransferEmail: e.target.value === 'E-transfer' && !x.etransferEmail ? (profile.email || '') : x.etransferEmail,
                  }))}>
                  <MenuItem value=""><em>Not chosen yet</em></MenuItem>
                  <MenuItem value="E-transfer">E-transfer</MenuItem>
                  <MenuItem value="Cheque">Cheque</MenuItem>
                </Select>
              </FormControl>
              {engagement.paymentMethod === 'E-transfer' && (
                <TextField label="E-transfer email" size="small" value={engagement.etransferEmail} onChange={setE('etransferEmail')}
                  sx={{ gridColumn: { sm: 'span 2' } }} helperText="Where to send their e-transfer — they confirm or change it on their link." />
              )}
            </Box>
            <ContractPanel leader={leader} onEmailContract={() => onEmail('contract')} onChanged={onChanged} onError={setError} />
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'space-between' }}>
        {confirmRemove ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2">Remove from this event (and delete their workshops)? Their profile is kept.</Typography>
            <Button size="small" color="error" variant="contained" onClick={handleRemove} disabled={saving}>Remove</Button>
            <Button size="small" onClick={() => setConfirmRemove(false)}>Keep</Button>
          </Box>
        ) : (
          <Button color="error" onClick={() => setConfirmRemove(true)}>Remove from event</Button>
        )}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {savedMsg && <Typography variant="body2" color="success.main">{savedMsg} ✓</Typography>}
          <Button onClick={onClose}>Close</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving || !profile.name.trim()}>{saving ? <CircularProgress size={18} /> : 'Save'}</Button>
        </Box>
      </DialogActions>

      {editingWorkshop && (
        <WorkshopDialog workshop={editingWorkshop} engagementId={leader.engagementId} equipmentOptions={equipmentOptions}
          onClose={() => setEditingWorkshop(null)} onSaved={() => { setEditingWorkshop(null); onChanged(); }} />
      )}

      {emptied && (
        <Dialog open onClose={() => setEmptied(null)} maxWidth="sm" fullWidth>
          <DialogTitle>Taken off the schedule</DialogTitle>
          <DialogContent>
            <Typography variant="body2" sx={{ mb: 1.5 }}>
              {leader.profile.name} has declined, so their workshop{emptied.length === 1 ? ' was' : 's were'} removed from the schedule. These spaces are now empty:
            </Typography>
            <Stack spacing={1}>
              {emptied.map((slot, i) => (
                <Paper key={i} variant="outlined" sx={{ p: 1.25 }}>
                  <Typography variant="body2" fontWeight={600}>{slot.sessionName} · {slot.spaceName}</Typography>
                  <Typography variant="caption" color="text.secondary" display="block">Was: {slot.title}</Typography>
                  {(slot.dancers > 0 || slot.supervisors > 0) && (
                    <Typography variant="caption" color="warning.main" display="block">
                      {[slot.dancers && `${slot.dancers} dancer${slot.dancers === 1 ? '' : 's'}`, slot.supervisors && `${slot.supervisors} supervisor${slot.supervisors === 1 ? '' : 's'}`].filter(Boolean).join(' and ')}
                      {' '}had been assigned here and now need{slot.dancers + slot.supervisors === 1 ? 's' : ''} a new workshop.
                    </Typography>
                  )}
                </Paper>
              ))}
            </Stack>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1.5 }}>
              Fill the spaces on the Schedule page.
            </Typography>
          </DialogContent>
          <DialogActions><Button variant="contained" onClick={() => setEmptied(null)}>OK</Button></DialogActions>
        </Dialog>
      )}
    </Dialog>
  );
}

// The leader's contract: where it's at, and the actions for each step.
// Saving compensation/sessions first matters — the contract is filled in
// from what's saved.
function ContractPanel({ leader, onEmailContract, onChanged, onError }) {
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const signedOn = leader.contractSignedAt ? new Date(leader.contractSignedAt).toLocaleDateString('en-CA', { dateStyle: 'long' }) : '';

  const setSigned = async (file) => {
    if (file && (file.type !== 'application/pdf' || file.size > 10 * 1024 * 1024)) { onError('The signed contract must be a PDF, 10MB or smaller.'); return; }
    setBusy(true);
    try { await adminClient.setSignedContract(leader.engagementId, file); onChanged(); } catch (err) { onError(err.message); } finally { setBusy(false); }
  };

  return (
    <Box sx={{ mb: 3 }}>
      <Typography fontWeight={600} sx={{ mb: 1 }}>Contract</Typography>
      <Typography variant="body2" sx={{ mb: 1.5 }}>
        {leader.contractStatus === 'Signed'
          ? (leader.contractSignedName
            ? `Signed online by ${leader.contractSignedName}${signedOn ? ` on ${signedOn}` : ''}.`
            : `Signed copy uploaded${signedOn ? ` on ${signedOn}` : ''}.`)
          : leader.contractStatus === 'Sent'
            ? 'Sent — waiting for them to sign on their private link.'
            : 'Not sent yet. Check compensation and workshops are saved, then email it.'}
      </Typography>
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        <Button size="small" variant="outlined" onClick={() => adminClient.openLeaderContractPdf(leader.engagementId)}>Preview contract</Button>
        {leader.contractStatus !== 'Signed' && (
          <Button size="small" variant="outlined" startIcon={<MailOutlineIcon />} onClick={onEmailContract}>
            {leader.contractStatus === 'Sent' ? 'Re-send contract' : 'Email contract'}
          </Button>
        )}
        {leader.hasSignedContract && <Button size="small" onClick={() => adminClient.openSignedContract(leader.engagementId)}>Open signed copy</Button>}
        <input ref={fileRef} type="file" accept="application/pdf" hidden onChange={e => { const f = e.target.files?.[0]; e.target.value = ''; if (f) setSigned(f); }} />
        <Button size="small" onClick={() => fileRef.current?.click()} disabled={busy}>
          {busy ? 'Uploading…' : leader.hasSignedContract ? 'Replace signed copy' : 'Upload signed copy'}
        </Button>
        {leader.hasSignedContract && <Button size="small" color="error" onClick={() => setSigned(null)} disabled={busy}>Remove signed copy</Button>}
      </Box>
    </Box>
  );
}

function WorkshopDialog({ workshop, engagementId, equipmentOptions, onClose, onSaved }) {
  const [value, setValue] = useState(workshop);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const isNew = !workshop.workshopId;

  const run = async (fn) => {
    setSaving(true); setError(null);
    try { await fn(); onSaved(); } catch (err) { setError(err.message); setSaving(false); }
  };

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{isNew ? 'Add workshop' : 'Edit workshop'}</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Box sx={{ pt: 1 }}>
          <WorkshopFieldsEditor value={value} onChange={setValue} equipmentOptions={equipmentOptions} showAdminFields />
        </Box>
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'space-between' }}>
        {!isNew ? (
          <Button color="error" startIcon={<DeleteOutlineIcon />} disabled={saving}
            onClick={() => run(() => adminClient.deleteWorkshop(workshop.workshopId))}>Delete</Button>
        ) : <span />}
        <Box>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="contained" disabled={saving || !value.title.trim()}
            onClick={() => run(() => (isNew ? adminClient.addWorkshop(engagementId, value) : adminClient.saveWorkshop(workshop.workshopId, value)))}>
            {saving ? <CircularProgress size={18} /> : 'Save'}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
}


// This event's leaders' t-shirt sizes and dietary needs (entered on their
// link or the Profile tab), with size counts and a copy-to-clipboard list.
// Declined leaders are listed but left out of the counts.
function SizesDietaryDialog({ open, onClose, leaders }) {
  const [copied, setCopied] = useState(false);
  useEffect(() => { if (open) setCopied(false); }, [open]);
  const rows = [...leaders].sort((a, b) => a.profile.name.localeCompare(b.profile.name));
  const counting = rows.filter(l => l.status !== 'Declined');
  const counts = new Map();
  counting.forEach(l => { const s = l.profile.tshirtSize || 'Not chosen'; counts.set(s, (counts.get(s) || 0) + 1); });
  const sizeOrder = [...TSHIRT_SIZES, 'Not chosen'];
  const sizes = [...counts].sort((a, b) => sizeOrder.indexOf(a[0]) - sizeOrder.indexOf(b[0]));
  const withDietary = counting.filter(l => l.profile.dietary && l.profile.dietary !== 'None');

  const copy = async () => {
    const lines = [
      'Name\tStatus\tT-shirt\tDietary',
      ...rows.map(l => [l.profile.name, l.status, l.profile.tshirtSize || '', l.profile.dietary || ''].join('\t')),
    ];
    try { await navigator.clipboard.writeText(lines.join('\n')); setCopied(true); } catch { setCopied(false); }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>T-shirts & dietary</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', alignItems: 'center', mb: 1 }}>
          <Typography variant="body2" fontWeight={500}>T-shirts:</Typography>
          {sizes.map(([size, n]) => (
            <Chip key={size} size="small" color={size === 'Not chosen' ? 'warning' : 'default'} variant="outlined" label={`${size}: ${n}`} />
          ))}
        </Box>
        <Typography variant="body2" sx={{ mb: 2 }}>
          {withDietary.length
            ? `${withDietary.length} leader${withDietary.length === 1 ? ' has' : 's have'} dietary needs.`
            : 'No dietary needs listed.'}
          <Typography component="span" variant="caption" color="text.secondary"> Counts leave out leaders who declined.</Typography>
        </Typography>
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Leader</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>T-shirt</TableCell>
                <TableCell>Dietary</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map(l => (
                <TableRow key={l.engagementId} sx={l.status === 'Declined' ? { opacity: 0.5 } : undefined}>
                  <TableCell>{l.profile.name}</TableCell>
                  <TableCell><Chip size="small" label={l.status} color={STATUS_COLOR[l.status]} variant="outlined" /></TableCell>
                  <TableCell>{l.profile.tshirtSize || <Typography variant="caption" color="warning.main">Not chosen</Typography>}</TableCell>
                  <TableCell>{l.profile.dietary || <Typography variant="caption" color="text.disabled">—</Typography>}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>
      <DialogActions>
        {copied && <Typography variant="body2" color="success.main" sx={{ mr: 1 }}>Copied ✓</Typography>}
        <Button onClick={copy}>Copy to clipboard</Button>
        <Button variant="contained" onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

// The equipment checklist used by workshops (and by spaces, later).
function EquipmentDialog({ open, onClose, options, onSaved }) {
  const [list, setList] = useState(options);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  useEffect(() => { if (open) { setList(options); setDraft(''); setError(null); } }, [open, options]);

  const add = () => {
    const v = draft.trim();
    if (v && !list.some(x => x.toLowerCase() === v.toLowerCase())) setList(l => [...l, v]);
    setDraft('');
  };
  const unchanged = useMemo(() => list.join('|') === options.join('|'), [list, options]);

  const handleSave = async () => {
    setSaving(true); setError(null);
    try {
      const { equipmentOptions } = await adminClient.saveWorkshopSettings({ equipmentOptions: list });
      onSaved(equipmentOptions);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Equipment list</DialogTitle>
      <DialogContent>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
          The checklist leaders tick for what their workshop needs. Spaces will use the same list, so the schedule can warn about mismatches.
        </Typography>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
          {list.map(item => <Chip key={item} label={item} onDelete={() => setList(l => l.filter(x => x !== item))} />)}
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <TextField size="small" fullWidth placeholder="Add an item (e.g. Sprung floor)" value={draft}
            onChange={e => setDraft(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); add(); } }} inputProps={{ maxLength: 40 }} />
          <Button onClick={add} disabled={!draft.trim()}>Add</Button>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving || unchanged || list.length === 0}>{saving ? <CircularProgress size={18} /> : 'Save'}</Button>
      </DialogActions>
    </Dialog>
  );
}
